import type { PricePromotionType, PromotionType } from "../types/CommonTypes";

export enum DiscountPriceApplicability {
    NetPrice = 1,
    Tax = 2,
    ListPrice = 3,
}

// instead of inheriting of IDiscount
export enum DiscountType {
    Percentage = 0,
    FixedPrice = 1,
    SpotLight = 3,
}

export type Price = {
    PriceInVat: number;
    PriceExVat?: number;
};

export type Discount = {
    ID: string;
    Type: DiscountType;
    ApplicableToPrice: DiscountPriceApplicability;
    CustomerOptInRequired: boolean;
    IsAlreadyApplied: boolean;
    Value: number;
};

// For readability, c# has more unused values
type Promotion = Discount;

type AppliedDiscount = {
    Discount: Discount;
    AppliedValue: number;
};

export type PromotionalPrice = {
    NetPrice: number;
    Tax: number;
    ListPrice: number;
    NetPriceDiscount: number;
    TaxDiscount: number;
    ListPriceDiscount: number;
    NetPriceWithDiscount: number;
    TaxWithDiscount: number;
    ListPriceWithDiscount: number;
    SpotLights: Promotion[];
    Discounts: AppliedDiscount[];
};

type PromotionalPriceKeysWithNumberValues = {
    [K in keyof PromotionalPrice]: PromotionalPrice[K] extends number ? K : never;
}[keyof PromotionalPrice];

const convertPromoType = (type: string): DiscountType =>
    type === "PERCENTAGEDISCOUNT"
        ? DiscountType.Percentage
        : type === "FIXEDPRICEDISCOUNT"
          ? DiscountType.FixedPrice
          : DiscountType.SpotLight;

export const convertPromoToDiscountType = (
    promotions: PricePromotionType[],
    allPromotions: PromotionType[],
): Discount[] => {
    return promotions.map((promo) => ({
        Type: convertPromoType(promo.type.toUpperCase()),
        ApplicableToPrice: DiscountPriceApplicability.ListPrice,
        Value: allPromotions?.find((item) => item.id === promo.id)?.amount || 0,
        ID: promo.id,
        CustomerOptInRequired: false,
        IsAlreadyApplied: false,
    }));
};

type FunctionsType = {
    getPriceWithoutDiscounts: (price: number, alreadyAppliedDiscounts: Discount[]) => number;
    removeAlreadyAppliedDiscounts: (priceInfo: PromotionalPrice, discountsToBeRemoved: Discount[]) => void;
    getDiscount: (price: number, discounts: Discount[]) => number;
    applyDiscounts: (priceInfo: PromotionalPrice, discountsToBeApplied: Discount[]) => void;
    createPromotionalPrice: (
        price: Price,
        discountsToBeRemoved: Discount[],
        discountsToBeApplied: Discount[],
    ) => PromotionalPrice;
    calculateDiscountedPrices: (
        price: Price,
        applicablePromotions: Discount[],
        customerOptInDiscounts?: Discount[],
    ) => PromotionalPrice;
};
// necessary for mocking helpers in the same js file
const functions: FunctionsType = {} as FunctionsType;

// Helpers

// According to c# documentation the decimal type actually uses ceil? -> weird effects
const roundDecimal = (value: number): number => parseFloat(value.toFixed(2));

// Instead of constructor / default values
const getInitialPromotionalPrice = (): PromotionalPrice => ({
    ...{
        NetPrice: 0,
        Tax: 0,
        ListPrice: 0,
        NetPriceDiscount: 0,
        TaxDiscount: 0,
        ListPriceDiscount: 0,
        NetPriceWithDiscount: 0,
        TaxWithDiscount: 0,
        ListPriceWithDiscount: 0,
        SpotLights: [],
        Discounts: [],
    },
});

// Instead of the c# PromotionalPrice.TaxRate property
const getTaxRate = (priceInfo: PromotionalPrice): number => {
    if (!priceInfo.NetPrice || !priceInfo.ListPrice) return 0;
    return priceInfo.ListPrice / priceInfo.NetPrice - 1;
};

// Instead of the c# PromotionalPrice.NetPriceWithDiscount property
const getNetPriceWithDiscount = (priceInfo: PromotionalPrice): number => {
    return priceInfo.NetPrice - priceInfo.NetPriceDiscount;
};

// Instead of the c# PromotionalPrice.TaxWithDiscount property
const getTaxWithDiscount = (priceInfo: PromotionalPrice): number => {
    return roundDecimal(getNetPriceWithDiscount(priceInfo) * getTaxRate(priceInfo)) - priceInfo.TaxDiscount;
};

// Instead of the c# PromotionalPrice.ListPriceWithDiscount property
const getListPriceWithDiscount = (priceInfo: PromotionalPrice): number => {
    if (!getTaxRate(priceInfo)) return priceInfo.ListPrice - priceInfo.ListPriceDiscount;
    return getNetPriceWithDiscount(priceInfo) + getTaxWithDiscount(priceInfo) - priceInfo.ListPriceDiscount;
};

// Main Flow

// calculate total amount to be undone
functions.getPriceWithoutDiscounts = (price, alreadyAppliedDiscounts) => {
    const sumOfPercentages = alreadyAppliedDiscounts
        .filter((discount) => discount.Type === DiscountType.Percentage)
        .reduce((acc, discount) => acc + discount.Value, 0);
    const sumOfFixedPriceDiscounts = alreadyAppliedDiscounts
        .filter((discount) => discount.Type === DiscountType.FixedPrice)
        .reduce((acc, discount) => acc + discount.Value, 0);
    return price / (1 - sumOfPercentages) + sumOfFixedPriceDiscounts;
};

functions.removeAlreadyAppliedDiscounts = (priceInfo, alreadyAppliedDiscounts) => {
    priceInfo.ListPrice = functions.getPriceWithoutDiscounts(
        priceInfo.ListPrice,
        alreadyAppliedDiscounts.filter(
            (discount) => discount.ApplicableToPrice === DiscountPriceApplicability.ListPrice,
        ),
    );
    if (!priceInfo.NetPrice) return; // in case no netPrice is available, then we can stop right here

    priceInfo.Tax = priceInfo.ListPrice - priceInfo.NetPrice;
    const newTax = priceInfo.Tax;

    priceInfo.Tax = functions.getPriceWithoutDiscounts(
        priceInfo.Tax,
        alreadyAppliedDiscounts.filter((discount) => discount.ApplicableToPrice === DiscountPriceApplicability.Tax),
    );
    const taxDifference = priceInfo.Tax - newTax;
    const newTaxRate = priceInfo.Tax / (priceInfo.ListPrice + taxDifference - priceInfo.Tax);

    priceInfo.NetPrice = functions.getPriceWithoutDiscounts(
        priceInfo.NetPrice,
        alreadyAppliedDiscounts.filter(
            (discount) => discount.ApplicableToPrice === DiscountPriceApplicability.NetPrice,
        ),
    );
    priceInfo.ListPrice = priceInfo.NetPrice * (1 + newTaxRate);
    priceInfo.Tax = priceInfo.ListPrice - priceInfo.NetPrice;
};

// calculate total discount
functions.getDiscount = (price, discounts) => {
    const sumOfPercentages = discounts
        .filter((discount) => discount.Type === DiscountType.Percentage)
        .reduce((acc, discount) => acc + discount.Value, 0);
    const sumOfFixedPriceDiscounts = discounts
        .filter((discount) => discount.Type === DiscountType.FixedPrice)
        .reduce((acc, discount) => acc + discount.Value, 0);
    return price * sumOfPercentages + sumOfFixedPriceDiscounts;
};

// also undoes discounts!
functions.applyDiscounts = (priceInfo, discountsToBeApplied) => {
    let newListPrice = priceInfo.ListPrice;
    if (priceInfo.NetPrice) {
        discountsToBeApplied
            .filter((discount) => discount.ApplicableToPrice === DiscountPriceApplicability.NetPrice)
            .forEach((discount) =>
                priceInfo.Discounts.push({
                    Discount: discount,
                    AppliedValue: functions.getDiscount(priceInfo.NetPrice, [discount]),
                }),
            );

        priceInfo.NetPriceDiscount = functions.getDiscount(
            priceInfo.NetPrice,
            discountsToBeApplied.filter(
                (discount) => discount.ApplicableToPrice === DiscountPriceApplicability.NetPrice,
            ),
        );

        const newTax = roundDecimal(getNetPriceWithDiscount(priceInfo) * getTaxRate(priceInfo));

        discountsToBeApplied
            .filter((discounts) => discounts.ApplicableToPrice === DiscountPriceApplicability.Tax)
            .forEach((discount) =>
                priceInfo.Discounts.push({
                    Discount: discount,
                    AppliedValue: functions.getDiscount(newTax, [discount]),
                }),
            );

        priceInfo.TaxDiscount = functions.getDiscount(
            newTax,
            discountsToBeApplied.filter((discount) => discount.ApplicableToPrice === DiscountPriceApplicability.Tax),
        );
        newListPrice = getNetPriceWithDiscount(priceInfo) + getTaxWithDiscount(priceInfo);
    }

    discountsToBeApplied
        .filter((discount) => discount.ApplicableToPrice === DiscountPriceApplicability.ListPrice)
        .forEach((discount) =>
            priceInfo.Discounts.push({
                Discount: discount,
                AppliedValue: functions.getDiscount(newListPrice, [discount]),
            }),
        );
    priceInfo.ListPriceDiscount = functions.getDiscount(
        newListPrice,
        discountsToBeApplied.filter((discount) => discount.ApplicableToPrice === DiscountPriceApplicability.ListPrice),
    );
};

// discountsToBeApplied should also contain all discountsToBeRemoved!
// switched order of discountsToBeApplied, discountsToBeRemoved
functions.createPromotionalPrice = (price, discountsToBeApplied, discountsToBeRemoved) => {
    const priceInfo: PromotionalPrice = {
        ...getInitialPromotionalPrice(),
        ListPrice: price.PriceInVat,
    };

    if (price.PriceExVat) {
        priceInfo.NetPrice = price.PriceExVat;
        priceInfo.Tax = priceInfo.ListPrice - priceInfo.NetPrice;
    }

    if (discountsToBeRemoved.length > 0) functions.removeAlreadyAppliedDiscounts(priceInfo, discountsToBeRemoved);
    if (discountsToBeApplied.length > 0) functions.applyDiscounts(priceInfo, discountsToBeApplied);

    // in c# these are dynamic properties
    priceInfo.NetPriceWithDiscount = getNetPriceWithDiscount(priceInfo);
    priceInfo.TaxWithDiscount = getTaxWithDiscount(priceInfo);
    priceInfo.ListPriceWithDiscount = getListPriceWithDiscount(priceInfo);

    Object.entries(priceInfo).forEach(([key, value]) => {
        if (typeof value === "number") {
            const typedKey = key as PromotionalPriceKeysWithNumberValues;
            priceInfo[typedKey] = roundDecimal(value);
        }
    });

    return priceInfo;
};

// main function
functions.calculateDiscountedPrices = (price, applicablePromotions, customerOptInDiscounts = []) => {
    if (customerOptInDiscounts.length > 0 && customerOptInDiscounts.filter((x) => !x.CustomerOptInRequired).length > 0)
        throw new Error("ContainsNoneCustomerOptInDiscountsException");

    // gather all none customer opt in discounts + the additional customer opt in discounts listed in customerOptInDiscounts
    const discountsToBeApplied = applicablePromotions.filter((promo) => {
        // difference with source: added a isAlreadyApplied check here!
        return (
            !promo.CustomerOptInRequired ||
            customerOptInDiscounts.filter((discount) => discount.ID === promo.ID).length > 0
        );
    });

    const discountsToBeRemoved = applicablePromotions.filter((promo) => {
        return promo.IsAlreadyApplied;
    });

    const info = functions.createPromotionalPrice(price, discountsToBeApplied, discountsToBeRemoved);
    info.SpotLights = applicablePromotions.filter((promo) => promo.Type === DiscountType.SpotLight);

    return info;
};

// export functions as an object, necessary for mocking
export { functions };
